/* eslint-disable max-len */
/**
 * @fileoverview Step 1: Mission Briefing — "Let's Create Something"
 *               Captures format, platforms, and workflow type (single/stream) in one step.
 *               PATH: src/components/contenthub/WorkflowTypeSelector.js
 */

import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FiLayout, FiLayers, FiVideo, FiCamera, FiFilm,
    FiFacebook, FiInstagram, FiAlertCircle, FiCheckCircle,
    FiArrowRight, FiArrowLeft, FiFileText, FiRefreshCw, FiLoader,
    FiAlignLeft, FiMenu, FiMoreHorizontal,
} from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';
import { useToast } from '../shared/contexts/ToastContext';
import {
    FORMATS, FREQUENCIES, TONES, LONGEVITY_OPTIONS,
    getTemplateIdsForFormat, getFormatConfig, isFormatSupportedForWorkflow,
} from '../shared/constants/templateConstants';
import { MISSION_BRIEFING_COPY } from '../shared/constants/workflowCopy';

// --- Icon map (string name → component) ---
const ICON_MAP = {
    FiLayout: <FiLayout className="h-6 w-6" />,
    FiLayers: <FiLayers className="h-6 w-6" />,
    FiVideo: <FiVideo className="h-6 w-6" />,
    FiCamera: <FiCamera className="h-6 w-6" />,
    FiFilm: <FiFilm className="h-6 w-6" />,
};

const LONGEVITY_ICON_MAP = {
    FiMoreHorizontal: <FiMoreHorizontal />,
    FiMenu: <FiMenu />,
    FiAlignLeft: <FiAlignLeft />,
};

function WorkflowTypeSelector({ project, onWorkflowSelect }) {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const { addToast } = useToast();

    // --- State ---
    const [selectedFormat, setSelectedFormat] = useState(null);
    const [platforms, setPlatforms] = useState({ facebook: false, instagram: false, linkedin: false });
    const [workflow, setWorkflow] = useState(null);
    const [streamType, setStreamType] = useState('recurring');
    const [frequency, setFrequency] = useState(FREQUENCIES[0].value);
    const [count, setCount] = useState(5);
    const [toneOverride, setToneOverride] = useState('Inherit Project Tone');
    const [longevity, setLongevity] = useState('medium');

    // --- Platform connection detection ---
    const isFbConnected = !!project?.associatedPageId;
    const isIgConnected = !!project?.associatedInstagramId;
    const isLinkedInConnected = !!project?.associatedLinkedInId;

    // No auto-selection — user must actively choose platforms and workflow

    // --- Derived state ---
    const isCountValid = useMemo(() => {
        return streamType !== 'fixed' || (Number.isInteger(count) && count > 0 && count <= 50);
    }, [streamType, count]);

    const atLeastOnePlatform = platforms.facebook || platforms.instagram || platforms.linkedin;
    const selectedPlatforms = useMemo(() => Object.keys(platforms).filter(k => platforms[k]), [platforms]);

    // Carousel now supports both single and stream workflows

    const canProceed = useMemo(() => {
        return selectedFormat && atLeastOnePlatform && workflow && isCountValid;
    }, [selectedFormat, atLeastOnePlatform, workflow, isCountValid]);

    // --- Handlers ---
    const handleFormatSelect = useCallback((formatId) => {
        setSelectedFormat(formatId);
    }, []);

    const togglePlatform = useCallback((key) => {
        if (key === 'facebook' && !isFbConnected) {
            addToast(MISSION_BRIEFING_COPY.platformNotConnected('facebook'), 'warning');
            return;
        }
        if (key === 'instagram' && !isIgConnected) {
            addToast(MISSION_BRIEFING_COPY.platformNotConnected('instagram'), 'warning');
            return;
        }
        if (key === 'linkedin' && !isLinkedInConnected) {
            addToast(MISSION_BRIEFING_COPY.platformNotConnected('linkedin'), 'warning');
            return;
        }
        // Mutual exclusivity: LinkedIn ↔ Meta (Facebook/Instagram)
        setPlatforms(prev => {
            const newVal = !prev[key];
            if (key === 'linkedin' && newVal) {
                // Selecting LinkedIn → deselect Meta platforms
                return { facebook: false, instagram: false, linkedin: true };
            }
            if ((key === 'facebook' || key === 'instagram') && newVal) {
                // Selecting a Meta platform → deselect LinkedIn
                return { ...prev, [key]: true, linkedin: false };
            }
            return { ...prev, [key]: newVal };
        });
        // Clear incompatible format selection when platform changes
        setSelectedFormat(prev => {
            if (!prev) return prev;
            // LinkedIn: clear stories/reels
            if (key === 'linkedin') {
                if (prev.startsWith('story') || prev === 'reel') return null;
            }
            // When toggling a Meta platform: clear carousel if both FB+IG would be selected
            if ((key === 'facebook' || key === 'instagram') && prev === 'carousel') {
                const wouldHaveBoth = (key === 'facebook' && platforms.instagram) || (key === 'instagram' && platforms.facebook);
                if (wouldHaveBoth) return null;
            }
            return prev;
        });
    }, [isFbConnected, isIgConnected, isLinkedInConnected, addToast]);

    const handleSubmit = useCallback(() => {
        if (!selectedFormat) return;
        if (!atLeastOnePlatform) {
            addToast(MISSION_BRIEFING_COPY.platformMinimumWarning, 'warning');
            return;
        }
        if (workflow === 'stream' && !isCountValid) return;

        const formatConfig = getFormatConfig(selectedFormat);
        const templates = getTemplateIdsForFormat(selectedFormat, selectedPlatforms);

        const effectiveTone = toneOverride === 'Inherit Project Tone' ? project.brandVoiceTone : toneOverride;

        const config = {
            workflow,
            format: selectedFormat,
            platforms: selectedPlatforms,
            templates,
            mediaType: formatConfig.mediaType,
            isVertical: formatConfig.isVertical,
            isEphemeral: formatConfig.isEphemeral,
            tone: effectiveTone,
            longevity: selectedFormat === 'standard' ? longevity : null,
        };

        if (workflow === 'stream') {
            config.type = streamType;
            config.frequency = frequency;
            if (streamType === 'fixed') {
                config.count = count;
            }
        }

        onWorkflowSelect(config);
    }, [selectedFormat, atLeastOnePlatform, workflow, isCountValid, selectedPlatforms, streamType, frequency, count, toneOverride, longevity, project, onWorkflowSelect, addToast]);

    // --- Loading state while project loads ---
    if (!project) {
        return (
            <div className="bg-white/5 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/10 flex items-center justify-center min-h-[400px]">
                <FiLoader className="h-8 w-8 text-slate-blue animate-spin" />
            </div>
        );
    }

    // --- Render ---
    return (
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/10 relative overflow-hidden">

            {/* Decorator */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-slate-blue/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

            {/* --- Header & Back Button --- */}
            <div className="flex items-center mb-10 relative z-10">
                <button
                    onClick={() => navigate(`/project/${projectId}`)}
                    className="absolute left-0 p-3 text-cream/40 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                    title="Back to Project Overview"
                >
                    <FiArrowLeft className="h-6 w-6" />
                </button>
                <div className="w-full text-center">
                    <h2 className="text-2xl font-black text-cream tracking-tight uppercase">{MISSION_BRIEFING_COPY.header}</h2>
                    <p className="text-sm text-cream/50 mt-1 font-medium tracking-wide max-w-lg mx-auto">{MISSION_BRIEFING_COPY.subheader}</p>
                </div>
            </div>

            <div className="space-y-10 relative z-10 max-w-4xl mx-auto">

                {/* ================================================================ */}
                {/* SECTION A: "Where should it go?" — Platform Toggles              */}
                {/* ================================================================ */}
                <div>
                    <h3 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-5">{MISSION_BRIEFING_COPY.platformSectionTitle}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                        {/* Facebook */}
                        <div
                            onClick={() => togglePlatform('facebook')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlatform('facebook'); } }}
                            role="button"
                            tabIndex={0}
                            className={`
                                p-5 border rounded-2xl cursor-pointer transition-all duration-300 flex items-center justify-between
                                ${platforms.facebook ? 'border-blue-500/50 bg-blue-500/5 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20'}
                                ${!isFbConnected ? 'opacity-50' : ''}
                            `}
                        >
                            <div className="flex items-center">
                                <div className={`p-2.5 rounded-xl mr-3 transition-colors ${platforms.facebook ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' : 'bg-white/10 text-cream/40'}`}>
                                    <FiFacebook className="h-5 w-5" />
                                </div>
                                <div>
                                    <span className={`font-bold text-sm block ${platforms.facebook ? 'text-blue-400' : 'text-cream/60'}`}>Facebook</span>
                                    {!isFbConnected && (
                                        <span className="text-[10px] text-red-400 flex items-center gap-1 font-bold uppercase mt-0.5">
                                            <FiAlertCircle className="w-3 h-3" /> Not Connected
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${platforms.facebook ? 'bg-blue-500 border-blue-500 text-white' : 'border-white/20 bg-transparent'}`}>
                                {platforms.facebook && <FiCheckCircle className="h-3.5 w-3.5" />}
                            </div>
                        </div>

                        {/* Instagram */}
                        <div
                            onClick={() => togglePlatform('instagram')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlatform('instagram'); } }}
                            role="button"
                            tabIndex={0}
                            className={`
                                p-5 border rounded-2xl cursor-pointer transition-all duration-300 flex items-center justify-between
                                ${platforms.instagram ? 'border-pink-500/50 bg-pink-500/5 shadow-[0_0_15px_rgba(236,72,153,0.1)]' : 'border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20'}
                                ${!isIgConnected ? 'opacity-50' : ''}
                            `}
                        >
                            <div className="flex items-center">
                                <div className={`p-2.5 rounded-xl mr-3 transition-colors ${platforms.instagram ? 'bg-pink-500 text-white shadow-lg shadow-pink-500/30' : 'bg-white/10 text-cream/40'}`}>
                                    <FiInstagram className="h-5 w-5" />
                                </div>
                                <div>
                                    <span className={`font-bold text-sm block ${platforms.instagram ? 'text-pink-400' : 'text-cream/60'}`}>Instagram</span>
                                    {!isIgConnected && (
                                        <span className="text-[10px] text-red-400 flex items-center gap-1 font-bold uppercase mt-0.5">
                                            <FiAlertCircle className="w-3 h-3" /> Not Connected
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${platforms.instagram ? 'bg-pink-500 border-pink-500 text-white' : 'border-white/20 bg-transparent'}`}>
                                {platforms.instagram && <FiCheckCircle className="h-3.5 w-3.5" />}
                            </div>
                        </div>

                        {/* LinkedIn */}
                        <div
                            onClick={() => togglePlatform('linkedin')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlatform('linkedin'); } }}
                            role="button"
                            tabIndex={0}
                            className={`
                                p-5 border rounded-2xl cursor-pointer transition-all duration-300 flex items-center justify-between
                                ${platforms.linkedin ? 'border-[#0077B5]/50 bg-[#0077B5]/5 shadow-[0_0_15px_rgba(0,119,181,0.1)]' : 'border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20'}
                                ${!isLinkedInConnected ? 'opacity-50' : ''}
                            `}
                        >
                            <div className="flex items-center">
                                <div className={`p-2.5 rounded-xl mr-3 transition-colors ${platforms.linkedin ? 'bg-[#0077B5] text-white shadow-lg shadow-[#0077B5]/30' : 'bg-white/10 text-cream/40'}`}>
                                    <FaLinkedin className="h-5 w-5" />
                                </div>
                                <div>
                                    <span className={`font-bold text-sm block ${platforms.linkedin ? 'text-[#0077B5]' : 'text-cream/60'}`}>LinkedIn</span>
                                    {!isLinkedInConnected && (
                                        <span className="text-[10px] text-red-400 flex items-center gap-1 font-bold uppercase mt-0.5">
                                            <FiAlertCircle className="w-3 h-3" /> Not Connected
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${platforms.linkedin ? 'bg-[#0077B5] border-[#0077B5] text-white' : 'border-white/20 bg-transparent'}`}>
                                {platforms.linkedin && <FiCheckCircle className="h-3.5 w-3.5" />}
                            </div>
                        </div>
                    </div>
                    {/* Mutual exclusivity hint */}
                    {(platforms.linkedin || platforms.facebook || platforms.instagram) && (
                        <p className="text-[10px] text-cream/30 mt-2 max-w-2xl">
                            {platforms.linkedin ? 'LinkedIn posts are created separately from Meta (Facebook/Instagram).' : 'Meta platforms can be combined, but not with LinkedIn.'}
                        </p>
                    )}
                </div>

                {/* ================================================================ */}
                {/* SECTION B: "One post or a whole series?" — Workflow Picker        */}
                {/* Only shown after platform is selected                            */}
                {/* ================================================================ */}
                {atLeastOnePlatform && <div className="animate-fade-in-up">
                    <h3 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-5">{MISSION_BRIEFING_COPY.workflowSectionTitle}</h3>
                    <div className="flex gap-3 max-w-lg">
                        {/* Single Post Pill */}
                        <button
                            onClick={() => setWorkflow('single')}
                            className={`
                                flex-1 flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border text-sm font-bold transition-all duration-300
                                ${workflow === 'single'
                                    ? 'border-slate-blue bg-slate-blue/15 text-cream shadow-[0_0_15px_rgba(99,102,241,0.15)]'
                                    : 'border-white/10 bg-white/5 text-cream/50 hover:bg-white/10 hover:text-cream/80'}
                            `}
                        >
                            <FiFileText className="h-4 w-4" />
                            Single Post
                        </button>

                        {/* Content Series Pill */}
                        <button
                            onClick={() => setWorkflow('stream')}
                            className={`
                                flex-1 flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border text-sm font-bold transition-all duration-300
                                ${workflow === 'stream'
                                    ? 'border-pastel-green bg-pastel-green/15 text-cream shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                                    : 'border-white/10 bg-white/5 text-cream/50 hover:bg-white/10 hover:text-cream/80'}
                            `}
                        >
                            <FiRefreshCw className="h-4 w-4" />
                            Content Series
                        </button>
                    </div>

                    {/* --- Stream Sub-Form (only when Content Series selected) --- */}
                    {workflow === 'stream' && (
                        <div className="mt-6 p-6 border border-pastel-green/20 bg-pastel-green/5 rounded-2xl space-y-5 animate-fade-in-up max-w-lg">
                            {/* Stream Type: Recurring vs. Fixed */}
                            <div className="space-y-3">
                                <label className={`flex items-center p-3 rounded-xl border cursor-pointer transition-all ${streamType === 'recurring' ? 'bg-pastel-green/20 border-pastel-green/50' : 'bg-white/5 border-transparent hover:bg-white/10'}`}>
                                    <input type="radio" name="streamType" value="recurring" checked={streamType === 'recurring'} onChange={() => setStreamType('recurring')} className="text-pastel-green focus:ring-pastel-green bg-transparent border-white/20" />
                                    <span className={`ml-3 text-sm font-bold ${streamType === 'recurring' ? 'text-cream' : 'text-cream/60'}`}>Recurring Stream</span>
                                </label>
                                <label className={`flex items-center p-3 rounded-xl border cursor-pointer transition-all ${streamType === 'fixed' ? 'bg-pastel-green/20 border-pastel-green/50' : 'bg-white/5 border-transparent hover:bg-white/10'}`}>
                                    <input type="radio" name="streamType" value="fixed" checked={streamType === 'fixed'} onChange={() => setStreamType('fixed')} className="text-pastel-green focus:ring-pastel-green bg-transparent border-white/20" />
                                    <span className={`ml-3 text-sm font-bold ${streamType === 'fixed' ? 'text-cream' : 'text-cream/60'}`}>Fixed Number</span>
                                </label>
                            </div>

                            {/* Frequency Selector */}
                            <div>
                                <label htmlFor="frequency" className="block text-xs font-bold text-cream/40 uppercase tracking-widest mb-2">Frequency</label>
                                <div className="relative">
                                    <select
                                        id="frequency"
                                        value={frequency}
                                        onChange={(e) => setFrequency(e.target.value)}
                                        className="w-full bg-warm-dark/50 border border-white/10 text-white text-sm rounded-xl px-4 py-3 pr-10 appearance-none focus:outline-none focus:border-pastel-green/50 focus:ring-1 focus:ring-pastel-green/50"
                                        style={{ backgroundImage: 'none' }}
                                    >
                                        {FREQUENCIES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-cream/30">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                    </div>
                                </div>
                            </div>

                            {/* Count Input (for 'fixed' type) */}
                            {streamType === 'fixed' && (
                                <div>
                                    <label htmlFor="count" className="block text-xs font-bold text-cream/40 uppercase tracking-widest mb-2">Total Posts</label>
                                    <input
                                        type="number"
                                        id="count"
                                        value={count}
                                        onChange={(e) => setCount(parseInt(e.target.value, 10))}
                                        min="2"
                                        max="50"
                                        className="w-full bg-warm-dark/50 border border-white/10 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-pastel-green/50 focus:ring-1 focus:ring-pastel-green/50"
                                    />
                                    {!isCountValid && <p className="text-xs text-soft-coral mt-2 font-bold px-2">Please enter between 2 and 50 posts.</p>}
                                </div>
                            )}
                        </div>
                    )}
                </div>}

                {/* ================================================================ */}
                {/* SECTION C: "What are you making?" — Format Cards                 */}
                {/* Only shown after workflow type is selected                       */}
                {/* ================================================================ */}
                {atLeastOnePlatform && workflow && <div className="animate-fade-in-up">
                    <h3 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-5">{MISSION_BRIEFING_COPY.formatSectionTitle}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {FORMATS.filter(format => {
                            // LinkedIn doesn't support stories/reels — hide them
                            if (platforms.linkedin && format.isEphemeral) return false;
                            if (platforms.linkedin && (format.id === 'reel' || format.id === 'story_video')) return false;
                            // Both FB+IG selected: hide carousel (can't merge — different dimensions/strategy)
                            if (platforms.facebook && platforms.instagram && format.id === 'carousel') return false;
                            // LinkedIn only: hide carousel, stories, reels
                            if (platforms.linkedin && format.id === 'carousel') return false;
                            return true;
                        }).map((format) => {
                            const isSelected = selectedFormat === format.id;
                            const isDisabledForStream = workflow === 'stream' && !isFormatSupportedForWorkflow(format.id, 'stream');
                            const isDisabled = isDisabledForStream;

                            // Rename carousel to "Multi-Image" for FB-only
                            const isFbOnlyCarousel = format.id === 'carousel' && platforms.facebook && !platforms.instagram;
                            const displayLabel = isFbOnlyCarousel ? 'Multi-Image' : format.label;
                            const displayDesc = isFbOnlyCarousel ? 'Multiple images in a single post.' : format.desc;

                            const handleClick = () => {
                                if (!isDisabledForStream) handleFormatSelect(format.id);
                            };

                            return (
                                <div
                                    key={format.id}
                                    onClick={handleClick}
                                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) { e.preventDefault(); handleClick(); } }}
                                    role="button"
                                    tabIndex={isDisabled ? -1 : 0}
                                    title={isDisabledForStream ? MISSION_BRIEFING_COPY.carouselStreamNote : displayDesc}
                                    className={`
                                        p-4 border rounded-2xl cursor-pointer transition-all duration-300 flex flex-col items-center text-center group relative
                                        ${isDisabledForStream ? 'opacity-30 cursor-not-allowed border-white/5 bg-white/5' : ''}
                                        ${isSelected && !isDisabled ? 'border-slate-blue bg-slate-blue/10 shadow-[0_0_25px_rgba(99,102,241,0.25)] scale-[1.03]' : ''}
                                        ${!isSelected && !isDisabled ? 'border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20 hover:scale-[1.02]' : ''}
                                    `}
                                >
                                    <div className={`p-3 rounded-xl mb-3 transition-colors ${isSelected && !isDisabled ? 'bg-slate-blue text-white' : 'bg-white/5 text-slate-blue group-hover:bg-white/10'}`}>
                                        {ICON_MAP[format.iconName] || <FiLayout className="h-6 w-6" />}
                                    </div>
                                    <h4 className="text-sm font-bold text-cream mb-1">{displayLabel}</h4>
                                    <p className="text-[10px] text-cream/50 leading-tight">{displayDesc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>}

                {/* ================================================================ */}
                {/* SECTION D: Text Depth — only for Standard format                 */}
                {/* ================================================================ */}
                {selectedFormat === 'standard' && (
                    <div className="animate-fade-in-up max-w-lg">
                        <h3 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-4">Text Depth</h3>
                        <div className="grid grid-cols-3 gap-3">
                            {LONGEVITY_OPTIONS.map(opt => (
                                <div
                                    key={opt.id}
                                    onClick={() => setLongevity(opt.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLongevity(opt.id); } }}
                                    role="button"
                                    tabIndex={0}
                                    className={`p-4 rounded-2xl border cursor-pointer text-center transition-all ${longevity === opt.id ? 'border-slate-blue bg-slate-blue/20 shadow-glow' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                                >
                                    <div className={`flex justify-center mb-2 ${longevity === opt.id ? 'text-slate-blue' : 'text-cream/40'}`}>
                                        {LONGEVITY_ICON_MAP[opt.iconName] || <FiMenu />}
                                    </div>
                                    <p className={`text-sm font-bold ${longevity === opt.id ? 'text-white' : 'text-cream/60'}`}>{opt.label}</p>
                                    <p className="text-[10px] text-cream/40 mt-1 leading-tight">{opt.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ================================================================ */}
                {/* SECTION E: Tone                                                  */}
                {/* ================================================================ */}
                <div className="max-w-lg">
                    <h3 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-2">What tone should the AI use?</h3>
                    <p className="text-[10px] text-cream/30 mb-3">Leave on &apos;Inherit&apos; to use your project&apos;s default, or pick a different vibe.</p>
                    <div className="relative">
                        <select
                            value={toneOverride}
                            onChange={(e) => setToneOverride(e.target.value)}
                            className="w-full bg-warm-dark/50 border border-white/10 text-white text-sm rounded-2xl px-6 py-4 pr-12 appearance-none focus:outline-none focus:border-slate-blue/50 focus:ring-1 focus:ring-slate-blue/50"
                            style={{ backgroundImage: 'none' }}
                        >
                            {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-cream/30">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                        </div>
                    </div>
                </div>

                {/* ================================================================ */}
                {/* SUBMIT BUTTON                                                    */}
                {/* ================================================================ */}
                <div className="pt-2">
                    <button
                        onClick={handleSubmit}
                        disabled={!canProceed}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-95"
                    >
                        <FiArrowRight className="mr-2 h-5 w-5" />
                        {MISSION_BRIEFING_COPY.submitButton}
                    </button>
                </div>
            </div>
        </div>
    );
}

WorkflowTypeSelector.propTypes = {
    project: PropTypes.object,
    onWorkflowSelect: PropTypes.func.isRequired,
};

export default WorkflowTypeSelector;
